import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Pencil } from "lucide-react";
import { useUser, fetchWithAuth } from "../../Context/UserContext";
import API_URL from "../../config";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

const InfoSection = ({ title, canEdit, onEdit, children }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-foreground">{title}</h2>
        {canEdit && (
          <button onClick={onEdit} className="text-muted-foreground hover:text-foreground" aria-label="Editar">
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="mt-2 space-y-2 text-sm text-muted-foreground">{children}</div>
    </CardContent>
  </Card>
);

const CompanyInfo = ({ companyData, activeTab }) => {
  const { company, profile } = companyData;
  const { user } = useUser();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [averageScore, setAverageScore] = useState(null);
  const [numReviews, setNumReviews] = useState(null);
  const [scoreDistribution, setScoreDistribution] = useState({});
  const [selectedScores, setSelectedScores] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedReview, setExpandedReview] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const filterParam =
          selectedScores.length > 0
            ? `&filter_score=${selectedScores.join(",")}`
            : "";
        const response = await fetchWithAuth(
          `${API_URL}/review/company/${companyId}/reviews?page=${currentPage}${filterParam}`,
          { public: true }
        );
        const data = await response.json();

        if (response.ok) {
          setReviews(data.reviews || []);
          setAverageScore(data.puntaje?.promedio ?? 0);
          setNumReviews(data.puntaje?.num_reviews ?? 0);
          setScoreDistribution(data.score_distribution || {});
          setTotalPages(data.total_pages || 1);
        } else {
          console.error("Error al obtener reviews:", data.error);
        }
      } catch (error) {
        console.error("Error en la solicitud de reviews:", error);
      }
    };

    fetchReviews();
  }, [companyId, currentPage, selectedScores]);

  // Lógica para determinar si el usuario puede editar la información
  const canEdit =
    user?.companyUser?.id_company === parseInt(companyId) &&
    user?.companyUser?.tipo_cuenta === "Administrador";

  const handleScoreFilter = (score) => {
    setSelectedScores((prev) =>
      prev.includes(score) ? prev.filter((s) => s !== score) : [...prev, score]
    );
  };

  const scoreColors = {
    5: "#2A9D8F",
    4: "#4bbf73",
    3: "#eab308",
    2: "#f97316",
    1: "#d62246",
  };

  const renderPagination = () => (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
      >
        Atrás
      </Button>
      {currentPage > 2 && (
        <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)}>
          1
        </Button>
      )}
      {currentPage > 3 && <span className="text-muted-foreground">...</span>}
      {currentPage > 1 && (
        <Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage - 1)}>
          {currentPage - 1}
        </Button>
      )}
      <Button size="sm">{currentPage}</Button>
      {currentPage < totalPages && (
        <Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage + 1)}>
          {currentPage + 1}
        </Button>
      )}
      {currentPage < totalPages - 2 && <span className="text-muted-foreground">...</span>}
      {currentPage < totalPages - 1 && (
        <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)}>
          {totalPages}
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
      >
        Siguiente
      </Button>
    </div>
  );

  return (
    <div>
      {activeTab === "reviews" ? (
        <div className="space-y-4">
          {/* Mostrar puntaje promedio con cantidad de reseñas */}
          {averageScore !== null && numReviews > 0 ? (
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Puntaje Promedio:</strong> {averageScore.toFixed(1)} ⭐{" "}
              <span>
                ({numReviews} reseña{numReviews !== 1 ? "s" : ""})
              </span>
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Aún no hay reseñas para esta empresa.</p>
              {renderPagination()}
            </>
          )}

          {averageScore !== null && numReviews > 0 && (
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((score) => {
                const percentage = numReviews > 0 ? (scoreDistribution[score] / numReviews) * 100 : 0;

                return (
                  <div key={score} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedScores.includes(score)}
                      onChange={() => handleScoreFilter(score)}
                    />
                    <span className="w-14 text-muted-foreground">{score}-star</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full"
                        style={{ width: `${percentage}%`, backgroundColor: scoreColors[score] }}
                      ></div>
                    </div>
                    <span className="w-10 text-right text-muted-foreground">{Math.round(percentage)}%</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Mostrar reviews paginados */}
          {reviews.length > 0 && (
            <>
              <div className="space-y-4">
                {reviews.map((review) => (
                  <Card key={review.id_review}>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Por:</strong>{" "}
                        <a
                          href={`/profile/${review.creador_review.id_user}/${encodeURIComponent(
                            `${review.creador_review.nombre} ${review.creador_review.apellido}`
                          )}`}
                          className="text-primary hover:underline"
                        >
                          {review.creador_review.nombre} {review.creador_review.apellido}
                        </a>
                      </p>
                      <div className="mt-1 flex items-center justify-between">
                        <p className="flex items-center text-sm">
                          <span className="flex">
                            {Array(5)
                              .fill(0)
                              .map((_, index) => {
                                const roundedScore = Math.round(review.puntaje_global * 2) / 2; // Redondeo al 0.5 más cercano
                                const isFull = index + 1 <= Math.floor(roundedScore); // Estrella completamente llena
                                const isHalf = index + 1 === Math.ceil(roundedScore) && !isFull; // Media estrella

                                const starColors = ["#d62246", "#f97316", "#eab308", "#4bbf73", "#2A9D8F"];
                                const colorIndex =
                                  Math.min(Math.ceil(review.puntaje_global), starColors.length) - 1;

                                return (
                                  <span
                                    key={index}
                                    className="mr-0.5 inline-block h-5 w-5 rounded-full p-0.5 text-center text-xs"
                                    style={{
                                      background: isFull
                                        ? starColors[colorIndex]
                                        : isHalf
                                        ? `linear-gradient(90deg, ${starColors[colorIndex]} 50%, #ccc 50%)`
                                        : "#ccc",
                                      color: "white",
                                    }}
                                  >
                                    ★
                                  </span>
                                );
                              })}
                          </span>
                          <span className="ml-1 text-muted-foreground">: {review.puntaje_global.toFixed(1)}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">{review.created_at}</p>
                      </div>
                      <p className="mt-2 font-semibold text-foreground">{review.titulo}</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {expandedReview === review.id_review
                          ? review.comentario
                          : review.comentario.length > 500
                          ? review.comentario.slice(0, 500) + "..."
                          : review.comentario}
                        {review.comentario.length > 500 && expandedReview !== review.id_review && (
                          <button
                            onClick={() => setExpandedReview(review.id_review)}
                            className="ml-1 text-primary hover:underline"
                          >
                            Ver más
                          </button>
                        )}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        <strong className="text-foreground">Fecha de experiencia:</strong>{" "}
                        {review.fecha_experiencia}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Información de la Empresa */}
          <InfoSection
            title="Información de la Empresa"
            canEdit={canEdit}
            onEdit={() => navigate(`/company-profile/${companyId}/admin/info-empresa`)}
          >
            <p>
              <strong className="text-foreground">Sector:</strong> {company?.sector || <i>Pendiente definir</i>}
            </p>
            <p>
              <strong className="text-foreground">RUC:</strong> {company?.ruc || <i>Pendiente definir</i>}
            </p>
            <p>
              <strong className="text-foreground">Dirección:</strong>{" "}
              {profile?.direccion || <i>Pendiente definir</i>}
            </p>
            <p>
              <strong className="text-foreground">Certificaciones:</strong>{" "}
              {(() => {
                let certificaciones = profile?.certificaciones;

                // Si certificaciones es un string JSON, conviértelo a array
                if (typeof certificaciones === "string") {
                  try {
                    certificaciones = JSON.parse(certificaciones);
                  } catch (e) {
                    certificaciones = [];
                  }
                }

                // Si certificaciones no es un array, asignamos un array vacío
                if (!Array.isArray(certificaciones)) {
                  certificaciones = [];
                }

                return certificaciones.length > 0 ? certificaciones.join(", ") : <i>Pendiente definir</i>;
              })()}
            </p>
            <p>
              <strong className="text-foreground">Número de Empleados:</strong>{" "}
              {profile?.numero_empleados || <i>Pendiente definir</i>}
            </p>
          </InfoSection>

          {/* Descripción */}
          <InfoSection
            title="Descripción"
            canEdit={canEdit}
            onEdit={() => navigate(`/company-profile/${companyId}/admin/descripcion`)}
          >
            <p
              dangerouslySetInnerHTML={{
                __html: (profile?.descripcion || "<i>Descripción pendiente definir</i>").replace(/\n/g, "<br>"),
              }}
            />
          </InfoSection>

          {/* Datos de Contacto */}
          <InfoSection
            title="Datos de Contacto"
            canEdit={canEdit}
            onEdit={() => navigate(`/company-profile/${companyId}/admin/contacto`)}
          >
            <p>
              <strong className="text-foreground">Página web:</strong>{" "}
              {profile?.pagina_web ? (
                (() => {
                  let url = profile.pagina_web.trim(); // ✅ Eliminar espacios en blanco

                  // ✅ Agregar "https://" si no lo tiene
                  if (!url.startsWith("http://") && !url.startsWith("https://")) {
                    url = `https://${url}`;
                  }

                  // ✅ Agregar "www." si no lo tiene y si es un dominio de segundo nivel
                  try {
                    const urlObj = new URL(url);
                    if (!urlObj.hostname.startsWith("www.") && urlObj.hostname.split(".").length === 2) {
                      url = url.replace("//", "//www.");
                    }
                  } catch (error) {
                    console.error("URL inválida:", profile.pagina_web);
                  }

                  return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {profile.pagina_web}
                    </a>
                  );
                })()
              ) : (
                <i>Pendiente definir</i>
              )}
            </p>

            <p>
              <strong className="text-foreground">Teléfono de empresa:</strong>{" "}
              {profile?.telefono_empresa || <i>Pendiente definir</i>}
            </p>

            <div>
              <strong className="text-foreground">Información de Contacto:</strong>{" "}
              {(() => {
                let contactos = profile?.informacion_contacto;

                // Si `contactos` es un string JSON, conviértelo a un array
                if (typeof contactos === "string") {
                  try {
                    contactos = JSON.parse(contactos);
                  } catch (e) {
                    contactos = [];
                  }
                }

                // Asegurar que sea un array antes de hacer `.map()`
                if (!Array.isArray(contactos)) {
                  contactos = [];
                }

                return contactos.length > 0 ? (
                  <ul className="mt-1 list-disc pl-5">
                    {contactos.map((contact, index) => (
                      <li key={index}>
                        {`${contact.nombre} - Cel: ${contact.telefono} - Email: ${contact.email}`}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <i>No se han agregado contactos.</i>
                );
              })()}
            </div>

            <div>
              <strong className="text-foreground">Redes Sociales:</strong>{" "}
              {Array.isArray(profile?.redes_sociales) && profile.redes_sociales.length > 0 ? (
                <ul className="mt-1 list-disc pl-5">
                  {profile.redes_sociales.map((redSocial, index) => (
                    <li key={index}>
                      <a
                        href={redSocial.url.startsWith("http") ? redSocial.url : `http://${redSocial.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {redSocial.url}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <i>Por definir</i>
              )}
            </div>
          </InfoSection>

          {/* Servicios o Productos Ofrecidos */}
          <InfoSection
            title="Servicios o Productos Ofrecidos"
            canEdit={canEdit}
            onEdit={() => navigate(`/company-profile/${companyId}/admin/servicios`)}
          >
            {profile?.productos?.length > 0 || profile?.servicios?.length > 0 ? (
              <div>
                <p className="font-medium text-foreground">Productos:</p>
                <ul className="list-disc pl-5">
                  {profile?.productos?.map((producto, index) => (
                    <li key={index}>{producto}</li>
                  ))}
                </ul>
                <p className="mt-2 font-medium text-foreground">Servicios:</p>
                <ul className="list-disc pl-5">
                  {profile?.servicios?.map((servicio, index) => (
                    <li key={index}>{servicio}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p>
                <i>Pendiente definir</i>
              </p>
            )}
          </InfoSection>

          {/* Operación Geográfica */}
          <InfoSection
            title="Zonas de Operación"
            canEdit={canEdit}
            onEdit={() => navigate(`/company-profile/${companyId}/admin/zonas`)}
          >
            <p>
              <strong className="text-foreground">Áreas de Operación:</strong>{" "}
              {Array.isArray(profile?.operacion_geografica) ? (
                profile.operacion_geografica.join(", ")
              ) : (
                <i>Pendiente definir</i>
              )}
            </p>
          </InfoSection>
        </div>
      )}
    </div>
  );
};

export default CompanyInfo;
